import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import i18n from './i18n';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { ErrorBoundary } from './Components/ErrorBoundary';
import { reportClientError } from './utils/analytics';
import { detectLangFromPath, basenameFor, isRtl, rootRedirectPath } from './utils/locale';
import { readCachedCountrySync } from './utils/ipDetection';
import { installDomTranslateGuard } from './utils/domTranslateGuard';

// Before React renders: stop the browser auto-translate removeChild/insertBefore
// white-screen crash.
installDomTranslateGuard();

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.message && event.message.includes('listener indicated an asynchronous response')) return;
    reportClientError(event.error || new Error(event.message), { type: 'error' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const err = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    reportClientError(err, { type: 'unhandledrejection' });
  });
}

// ── URL-prefixed locales ─────────────────────────────────────────────────────────
// The URL prefix is the source of truth for the active language:
//   /            → English (no prefix)
//   /he/...      → Hebrew
//   /ar/...      → Arabic
//
// We compute basename from the current URL at mount, then set both i18n.language
// and document.documentElement.lang/dir to match. Internal navigation via react-router
// `<Link>` inherits the basename automatically — Link to="/pricing" becomes /he/pricing
// when basename is /he. The LanguageSwitcher hard-navigates (window.location.assign)
// rather than client-routing so basename is re-read on language change.
//
// Bots (Googlebot, GPTBot, ClaudeBot, puppeteer/react-snap, etc.) hitting /he/pricing
// see Hebrew content prerendered for that exact URL — that's what makes hreflang work.
// Fallback root locale-redirect for humans. The inline script in
// public/index.html normally handles this earlier and without a flash of
// English; this covers the rare case where that path was skipped (e.g.
// localStorage threw). Crawlers/prerender and already-localized URLs are
// excluded inside rootRedirectPath, so this never redirects a bot or loops.
let didRedirect = false;
try {
  let saved = null;
  try { saved = localStorage.getItem('stylon_company_lang'); } catch (_) { /* ignore */ }
  let cachedCountry = null;
  try { cachedCountry = readCachedCountrySync(); } catch (_) { /* ignore */ }
  const dest = rootRedirectPath(window.location.pathname, {
    savedLang: saved,
    navigatorLanguages: (navigator && navigator.languages)
      || (navigator && navigator.language ? [navigator.language] : []),
    userAgent: navigator && navigator.userAgent,
    cachedCountry,
  });
  if (dest) {
    didRedirect = true;
    window.location.replace(dest + window.location.search + window.location.hash);
  }
} catch (_) { /* never block boot on redirect logic */ }

if (!didRedirect) {
  const initialLang = detectLangFromPath(window.location.pathname);
  const initialBasename = basenameFor(initialLang);

  if (i18n.language && i18n.language.split('-')[0] !== initialLang) {
    i18n.changeLanguage(initialLang);
  }

  document.documentElement.setAttribute('lang', initialLang);
  document.documentElement.setAttribute('dir', isRtl(initialLang) ? 'rtl' : 'ltr');

  const rootEl = document.getElementById('root');

  const tree = (
    <HelmetProvider>
      <ErrorBoundary>
        <BrowserRouter basename={initialBasename || undefined}>
          <App />
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );

  // `hydrateRoot`, not the legacy `ReactDOM.hydrate`. react-snap pre-renders
  // each page with its API data already resolved, while the browser's first
  // render has none of it yet — so the two trees differ in how many children
  // they have. Legacy hydration does not recover from that: it walks the
  // server's nodes by POSITION and writes each client prop onto whatever node
  // sits at that index. On /pricing that put the trial banner's class and
  // test id onto the plans grid, so the grid stopped being a grid and the four
  // plan cards stacked down the page inside a purple banner. `hydrateRoot`
  // detects the mismatch and re-renders the subtree from scratch instead,
  // which costs a repaint and gets the right DOM.
  if (rootEl && rootEl.hasChildNodes()) {
    hydrateRoot(rootEl, tree);
  } else if (rootEl) {
    createRoot(rootEl).render(tree);
  }

  reportWebVitals();
}
